import PropTypes from 'prop-types';
import { usePermissions, MODULES, PERMISSIONS } from '../../hooks/usePermissions';
/**
 * PermissionGuard component for conditionally rendering content based on user permissions
 * 
 * @param {Object} props
 * @param {string} props.module - The module to check permissions for
 * @param {string|Array} props.permission - The permission(s) to check
 * @param {React.ReactNode} props.children - Content to render if permission is granted
 * @param {React.ReactNode} props.fallback - Optional fallback content to render if permission is denied
 * @param {boolean} props.requireAll - If true, requires all permissions (when permission is an array)
 * @param {boolean} props.invert - If true, renders children when permission is denied
 */
const PermissionGuard = ({ 
  module, 
  permission, 
  children, 
  fallback = null, 
  requireAll = false,
  invert = false 
}) => {
  const { hasPermission, hasAllPermissions, hasAnyPermission } = usePermissions();
  let hasAccess = false;
  if (Array.isArray(permission)) {
    hasAccess = requireAll 
      ? hasAllPermissions(module, permission)
      : hasAnyPermission(module, permission);
  } else {
    hasAccess = hasPermission(module, permission);
  }
  // Invert the logic if invert prop is true
  if (invert) {
    hasAccess = !hasAccess;
  }
  return hasAccess ? children : fallback;
};
PermissionGuard.propTypes = {
  module: PropTypes.oneOf(Object.values(MODULES)).isRequired,
  permission: PropTypes.oneOfType([
    PropTypes.oneOf(Object.values(PERMISSIONS)),
    PropTypes.arrayOf(PropTypes.oneOf(Object.values(PERMISSIONS)))
  ]).isRequired,
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  requireAll: PropTypes.bool,
  invert: PropTypes.bool
};
export default PermissionGuard; 